import React, { useState } from "react";
import {
  X,
  Calendar,
  IndianRupee,
  CreditCard,
  Upload,
  CheckCircle,
  FileText,
  AlertCircle,
  Clock
} from "lucide-react";
import "./PaymentModal.css";
import { markAsPaid } from "../../services/paymentService";

const PaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split("T")[0]);
  const [amount, setAmount] = useState(
    invoice?.BalanceAmount !== undefined ? parseFloat(invoice.BalanceAmount) : (invoice?.Amount !== undefined ? parseFloat(invoice.Amount) : "")
  );
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [proofImage, setProofImage] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }
      setFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!amount || !paidDate || !paymentMode) {
      setErrorMessage("Please fill all required fields");
      return;
    }

    const payload = {
      InvoiceId: invoice.InvoiceId,
      Amount: parseFloat(amount),
      PaymentMode: paymentMode,
      PaidDate: paidDate,
      ProofOfPaidImage: proofImage,
    };

    try {
      setLoading(true);
      const res = await markAsPaid(payload);
      if (res && res.success) {
        setIsSuccess(true);
        if (onSuccess) onSuccess(res);
        // Auto close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setErrorMessage(res?.message || "Payment submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error marking as paid:", error);
      const msg = typeof error === 'string' ? error : (error.message || "Something went wrong");
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payment-modal-backdrop" onClick={onClose}>
      <div className="payment-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="close-x-btn" onClick={onClose}>
          <X size={20} />
        </button>

        {isSuccess ? (
          <div className="success-content">
            <div className="success-anim-icon">
              <Clock size={60} color="#f59e0b" />
            </div>
            <h3>Waiting for Approval</h3>
            <p>Your payment has been submitted and is pending admin approval.</p>
            <button className="primary-action-btn success-close" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="modal-header-section">
              <div className="icon-badge">
                <CheckCircle size={24} color="#16a34a" />
              </div>
              <div className="header-text">
                <h3>Mark as Paid</h3>
                <p className="subtitle">
                  Invoice: <strong>{invoice.InvoiceNumber || invoice.InvoiceId}</strong>
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="payment-form">
              {errorMessage && (
                <div className="error-banner">
                  <AlertCircle size={16} />
                  <span>{errorMessage}</span>
                </div>
              )}
              <div className="form-grid-layout">
                <div className="form-group full-width">
                  <label>
                    <IndianRupee size={14} /> Amount Paid
                  </label>
                  <div className="input-with-icon">
                    <span className="prefix">₹</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <Calendar size={14} /> Paid Date
                  </label>
                  <input
                    type="date"
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <CreditCard size={14} /> Payment Mode
                  </label>
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    required
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Netbanking">Netbanking</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>
                    <FileText size={14} /> Proof of Payment (Optional)
                  </label>
                  <div className={`file-drop-zone ${proofImage ? 'has-file' : ''}`}>
                    <input type="file" accept="image/*" onChange={handleFileChange} />
                    {proofImage ? (
                      <div className="preview-container">
                        <img src={proofImage} alt="Proof" className="receipt-preview" />
                        <div className="file-meta">
                          <span>{fileName}</span>
                          <button type="button" className="remove-file" onClick={() => { setProofImage(""); setFileName(""); }}>
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="upload-btn-styled">
                          <Upload size={16} />
                          Choose File
                        </div>
                        <span className="upload-text-styled">
                          No file chosen
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-action-buttons">
                <button type="button" className="secondary-action-btn" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="primary-action-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="btn-loader"></span>
                      Processing...
                    </>
                  ) : (
                    "Confirm Payment"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
